import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';
import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';

/**
 * Защита от потери несохранённых изменений при уходе со страницы.
 *
 * `isDirty` — ГЕТТЕР: вызывается в момент навигации/закрытия вкладки, поэтому всегда читает
 * актуальное состояние формы (`form.isDirty()`, локальный флаг, `dirtyRef.current` и т.п.). Благодаря
 * этому намеренный уход (обнулить источник dirty прямо перед `navigate`) проходит без модалки —
 * отдельный escape-hatch не нужен. Каждая форма задаёт своё условие «есть несохранённое».
 *
 * Перехватывает:
 * - внутри-SPA навигацию (react-router `<Link>`/`navigate`: верхнее меню, меню аккаунта, вкладки,
 *   кнопка «назад») — через `useBlocker`; сравнение по `pathname` (смена запроса/хеша не мешает);
 * - закрытие вкладки/перезагрузку — нативным `beforeunload` (браузер покажет свой диалог; кастомный
 *   текст современные браузеры игнорируют).
 *
 * Модалка резолвит `blocker` при ЛЮБОМ закрытии (кнопки/Escape/оверлей/крестик), иначе навигация
 * зависла бы в состоянии `blocked`.
 */
/**
 * Тексты предупреждения. Умолчание — про несохранённые данные формы; экраны с другим смыслом ухода
 * (например, идущее подписание документа) задают свои, чтобы вопрос соответствовал происходящему.
 */
interface GuardTexts {
  readonly titleKey?: string;
  readonly textKey?: string;
}

export function useUnsavedChangesGuard(isDirty: () => boolean, texts: GuardTexts = {}): void {
  const { t } = useTranslation();
  const titleKey = texts.titleKey ?? 'common.leaveTitle';
  const textKey = texts.textKey ?? 'common.leaveText';

  // Геттер держим в ref и обновляем на каждом рендере — blocker и обработчик beforeunload читают
  // самое свежее условие, без устаревших замыканий.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: { pathname: string };
        nextLocation: { pathname: string };
      }) => isDirtyRef.current() && currentLocation.pathname !== nextLocation.pathname,
      [],
    ),
  );

  // id открытой модалки — служит и признаком «уже показана» (защита от повторного открытия).
  const modalIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (blocker.state !== 'blocked' || modalIdRef.current) {
      return;
    }
    let acted = false; // нажата кнопка (или сработала очистка); иначе закрытие трактуем как «остаться»
    modalIdRef.current = modals.openConfirmModal({
      title: t(titleKey),
      children: <Text size="sm">{t(textKey)}</Text>,
      labels: { confirm: t('common.leaveConfirm'), cancel: t('common.leaveCancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        acted = true;
        blocker.proceed?.();
      },
      onCancel: () => {
        acted = true;
        blocker.reset?.();
      },
      onClose: () => {
        modalIdRef.current = null;
        if (!acted) {
          blocker.reset?.();
        }
      },
    });
    // Принудительное размонтирование/смена blocker с открытой модалкой (напр. SSE-logout) — закрываем
    // её, чтобы не висела над следующим экраном. acted=true подавляет reset в onClose: на размонтировании
    // blocker снимается самим react-router.
    return () => {
      if (modalIdRef.current) {
        acted = true;
        modals.close(modalIdRef.current);
        modalIdRef.current = null;
      }
    };
  }, [blocker, t, titleKey, textKey]);

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (isDirtyRef.current()) {
        event.preventDefault();
        // Устаревшее, но некоторым браузерам всё ещё нужно, чтобы показать диалог.
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
}
